<script>
import richMarker from 'googlemaps-rich-marker-web';

const getTooltip = ({
  name, address, image, price, currency, priceLabel,
}) => `
  <div class="marker-tooltip">
    <img class="marker-tooltip__image" src=" ${image}" alt="${name}">
    <span class="marker-tooltip__name">${name}</span>
    <span class="marker-tooltip__address">${address}</span>
    ${currency !== '' ? `<span class="marker-tooltip__price"><span class="marker-tooltip__label">${priceLabel}:</span> ${price}  <span class="marker-tooltip__currency">${currency}</span></span>` : ''}
  </div>
`;

const getPriceMarker = ({ price, currency, color }) => `
  <div class="marker marker--price">
    <div class="marker__wrap marker__wrap--${color}">
      ${price} ${currency}
    </div>
  </div>
  `;

const getMarker = ({ id, color }) => `
  <div class="marker marker--index">
    <div class="marker__wrap marker__wrap--${color}">
      ${id}
    </div>
  </div>
  `;

const getEmptyMarker = () => '';

export const EVENTS = [
  'click',
  'rightclick',
  'dblclick',
  'drag',
  'dragstart',
  'dragend',
  'mouseup',
  'mousedown',
  'mouseover',
  'mouseout',
];

export default {
  name: 'GoogleMarker',

  inject: ['getInstance'],

  props: {
    lat: {
      required: true,
      type: Number,
    },

    lng: {
      required: true,
      type: Number,
    },

    id: {
      type: [String, Number],
      required: true,
    },

    price: {
      required: true,
      type: Number,
    },

    currency: {
      required: true,
      type: String,
    },

    address: {
      required: true,
      type: String,
    },

    name: {
      required: true,
      type: String,
    },

    image: {
      required: true,
      type: String,
    },

    isAvailable: {
      required: true,
      type: Boolean,
    },

    color: {
      required: true,
      type: String,
    },

    events: {
      required: false,
      type: Array,
      default: () => ['click'],
      validator(events) {
        return events.every((event) => EVENTS.includes(event));
      },
    },

    isPriceMode: {
      default: true,
      type: Boolean,
      required: false,
    },

    showUnavailable: {
      default: true,
      type: Boolean,
      required: false,
    },
  },

  data: () => ({
    marker: null,
  }),

  watch: {
    $props: {
      handler() {
        this.reInit();
      },
      deep: true,
      immediate: false,
    },
  },

  created() {
    this.addMarker();
  },

  destroyed() {
    if (this.marker === null) {
      return;
    }

    this.marker.setMap(null);
    this.$emit('destroyed', this.id);
  },

  methods: {
    async addMarker() {
      const { map, google } = this.getInstance();

      this.marker = new richMarker.RichMarker({
        position: new google.maps.LatLng(this.lat, this.lng),
        optimized: false,
        shadow: false,
        content: this.getMarkerHtml(),
        zIndex: 1,
      });

      this.events.forEach((eventName) => {
        this.marker.addListener(eventName, (event) => {
          this.$emit(eventName, {
            event,
            map,
            marker: this.marker,
            id: this.id,
          });
        });
      });

      this.marker.setMap(map);

      const infowindow = new google.maps.InfoWindow({
        pixelOffset: new google.maps.Size(0, -36),
        maxWidth: 220,
        content: getTooltip({
          name: this.name,
          address: this.address,
          image: this.image,
          price: this.price,
          currency: this.currency,
          priceLabel: this.t('map.priceLabel'),
        }),
      });

      this.marker.addListener('mouseover', () => {
        infowindow.open({
          anchor: this.marker,
          map,
          shouldFocus: false,
        });

        this.marker.addListener('mouseout', () => {
          infowindow.close();
        });
      });

      this.$emit('init', {
        map,
        google,
        id: this.id,
        marker: this.marker,
      });
    },

    getMarkerHtml() {
      const args = {
        id: this.id,
        price: this.price,
        currency: this.currency,
        name: this.name,
        image: this.image,
        address: this.address,
        color: this.color,
      };

      if (!this.showUnavailable && !this.isAvailable) {
        return getEmptyMarker();
      }

      return this.getMarker(args);
    },

    getMarker(args) {
      return this.isPriceMode
        ? getPriceMarker(args)
        : getMarker(args);
    },

    reInit() {
      this.marker.setMap(null);
      this.addMarker();
    },
  },

  render() {
    return '';
  },
};
</script>
